import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Clock, ChevronRight, CheckCircle, XCircle, Award, AlertCircle, Play, Loader2 } from 'lucide-react';
import { senvraApi } from '../utils/axiosInstance';

const CourseQuiz = () => {
  const { courseName } = useParams();
  const navigate = useNavigate();
  const decodedCourseName = decodeURIComponent(courseName);
  
  const [quizStarted, setQuizStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(30);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [score, setScore] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [answersReview, setAnswersReview] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [certificateInfo, setCertificateInfo] = useState(null);

  // Questions database for different courses (10 questions each)
  const questionBank = {
    'Web Development': [
      {
        id: 1,
        text: "What does HTML stand for?",
        options: [
          "Hyper Text Markup Language",
          "High Tech Modern Language",
          "Hyper Transfer Markup Language",
          "Home Tool Markup Language"
        ],
        correct: 0
      },
      {
        id: 2,
        text: "Which CSS property is used to change the text color of an element?",
        options: [
          "text-color",
          "color",
          "font-color",
          "text-style"
        ],
        correct: 1
      },
      {
        id: 3,
        text: "What does CSS stand for?",
        options: [
          "Creative Style Sheets",
          "Computer Style Sheets",
          "Cascading Style Sheets",
          "Colorful Style Sheets"
        ],
        correct: 2
      },
      {
        id: 4,
        text: "Which JavaScript keyword is used to declare a variable?",
        options: [
          "var",
          "let",
          "const",
          "All of the above"
        ],
        correct: 3
      },
      {
        id: 5,
        text: "What is the correct way to write a JavaScript array?",
        options: [
          "var colors = 'red', 'green', 'blue'",
          "var colors = (1:'red', 2:'green', 3:'blue')",
          "var colors = ['red', 'green', 'blue']",
          "var colors = {red, green, blue}"
        ],
        correct: 2
      },
      {
        id: 6,
        text: "Which HTML tag is used to link an external CSS file?",
        options: [
          "<css>",
          "<stylesheet>",
          "<link>",
          "<style>"
        ],
        correct: 2
      },
      {
        id: 7,
        text: "What does DOM stand for?",
        options: [
          "Document Object Model",
          "Data Object Model",
          "Document Oriented Model",
          "Display Object Management"
        ],
        correct: 0
      },
      {
        id: 8,
        text: "Which CSS property is used to make text bold?",
        options: [
          "font-bold",
          "text-bold",
          "font-weight",
          "bold-text"
        ],
        correct: 2
      },
      {
        id: 9,
        text: "What is the correct syntax for referring to an external script called 'script.js'?",
        options: [
          "<script href='script.js'>",
          "<script name='script.js'>",
          "<script src='script.js'>",
          "<script file='script.js'>"
        ],
        correct: 2
      },
      {
        id: 10,
        text: "Which company developed JavaScript?",
        options: [
          "Microsoft",
          "Netscape",
          "Google",
          "Apple"
        ],
        correct: 1
      }
    ],
    'Python Programming': [
      {
        id: 1,
        text: "What is the correct way to create a function in Python?",
        options: [
          "function myFunction():",
          "def myFunction():",
          "create myFunction():",
          "new myFunction():"
        ],
        correct: 1
      },
      {
        id: 2,
        text: "Which of the following is used to print output in Python?",
        options: [
          "console.log()",
          "printf()",
          "print()",
          "echo()"
        ],
        correct: 2
      },
      {
        id: 3,
        text: "What is the correct file extension for Python files?",
        options: [
          ".pyth",
          ".pt",
          ".py",
          ".python"
        ],
        correct: 2
      },
      {
        id: 4,
        text: "How do you create a variable with the floating number 2.8?",
        options: [
          "x = 2.8",
          "x = float(2.8)",
          "Both A and B",
          "x = 2.8f"
        ],
        correct: 2
      },
      {
        id: 5,
        text: "What is the correct syntax to output 'Hello World' in Python?",
        options: [
          "p('Hello World')",
          "echo 'Hello World'",
          "print('Hello World')",
          "console.log('Hello World')"
        ],
        correct: 2
      },
      {
        id: 6,
        text: "Which operator is used for exponentiation in Python?",
        options: [
          "^",
          "**",
          "^^",
          "exp"
        ],
        correct: 1
      },
      {
        id: 7,
        text: "What is the correct way to create a list in Python?",
        options: [
          "list = (1, 2, 3)",
          "list = {1, 2, 3}",
          "list = [1, 2, 3]",
          "list = <1, 2, 3>"
        ],
        correct: 2
      },
      {
        id: 8,
        text: "Which keyword is used to define a class in Python?",
        options: [
          "class",
          "Class",
          "define",
          "object"
        ],
        correct: 0
      },
      {
        id: 9,
        text: "What is the correct way to import a module in Python?",
        options: [
          "include module",
          "import module",
          "using module",
          "require module"
        ],
        correct: 1
      },
      {
        id: 10,
        text: "Which of the following is a Python tuple?",
        options: [
          "[1, 2, 3]",
          "{1, 2, 3}",
          "(1, 2, 3)",
          "<1, 2, 3>"
        ],
        correct: 2
      }
    ],
    'Cyber Security': [
      {
        id: 1,
        text: "What does CIA stand for in information security?",
        options: [
          "Confidentiality, Integrity, Availability",
          "Central Intelligence Agency",
          "Computer Internet Access",
          "Code Integrity Authentication"
        ],
        correct: 0
      },
      {
        id: 2,
        text: "What is phishing?",
        options: [
          "A type of computer virus",
          "A method to catch fish",
          "A fraudulent attempt to obtain sensitive information",
          "A network protocol"
        ],
        correct: 2
      },
      {
        id: 3,
        text: "What is a firewall?",
        options: [
          "A physical wall that prevents fire",
          "A network security system that monitors traffic",
          "A type of antivirus software",
          "A hardware component"
        ],
        correct: 1
      },
      {
        id: 4,
        text: "What is encryption?",
        options: [
          "Converting data into a secret code",
          "Deleting data permanently",
          "Copying data to another location",
          "Compressing data"
        ],
        correct: 0
      },
      {
        id: 5,
        text: "What is a DDoS attack?",
        options: [
          "Distributed Denial of Service",
          "Data Destruction of Service",
          "Direct Denial of Security",
          "Distributed Data Output System"
        ],
        correct: 0
      },
      {
        id: 6,
        text: "What is the purpose of HTTPS?",
        options: [
          "Secure communication over computer networks",
          "Faster website loading",
          "Better SEO rankings",
          "Email encryption"
        ],
        correct: 0
      },
      {
        id: 7,
        text: "What is a VPN used for?",
        options: [
          "To create a secure connection over the internet",
          "To increase internet speed",
          "To block all advertisements",
          "To backup data"
        ],
        correct: 0
      },
      {
        id: 8,
        text: "What is two-factor authentication?",
        options: [
          "Using two different passwords",
          "Using two different verification methods",
          "Having two separate accounts",
          "Two-step login process"
        ],
        correct: 1
      },
      {
        id: 9,
        text: "What is malware?",
        options: [
          "Malicious software",
          "Hardware malfunction",
          "Network protocol",
          "Security certificate"
        ],
        correct: 0
      },
      {
        id: 10,
        text: "What is social engineering?",
        options: [
          "Psychological manipulation of people",
          "Engineering social media platforms",
          "Building social networks",
          "Creating social apps"
        ],
        correct: 0
      }
    ],
    'Java Programming': [
    { id: 1, text: "Which component is used to compile, debug and execute Java programs?", options: ["JRE", "JIT", "JDK", "JVM"], correct: 2 },
    { id: 2, text: "Which keyword is used for accessing the features of a package?", options: ["package", "import", "extends", "export"], correct: 1 },
    { id: 3, text: "What is the default value of a boolean variable in Java?", options: ["true", "false", "null", "0"], correct: 1 },
    { id: 4, text: "Which of these is NOT a valid access modifier in Java?", options: ["public", "private", "protected", "internal"], correct: 3 },
    { id: 5, text: "Which class is the superclass of every class in Java?", options: ["String", "Object", "System", "ArrayList"], correct: 1 },
    { id: 6, text: "Which method is used to start a thread execution in Java?", options: ["init()", "start()", "run()", "resume()"], correct: 1 },
    { id: 7, text: "Which keyword is used to prevent method overriding?", options: ["static", "constant", "final", "abstract"], correct: 2 },
    { id: 8, text: "What is the size of an int variable in Java?", options: ["16-bit", "32-bit", "64-bit", "8-bit"], correct: 1 },
    { id: 9, text: "Which of these is used to handle exceptions in Java?", options: ["try-catch", "throw", "throws", "All of the above"], correct: 3 },
    { id: 10, text: "Which collection allows unique elements only?", options: ["List", "Vector", "Set", "Stack"], correct: 2 }
  ],
  'C Programming': [
    { id: 1, text: "Who is the father of C language?", options: ["Steve Jobs", "James Gosling", "Dennis Ritchie", "Rasmus Lerdorf"], correct: 2 },
    { id: 2, text: "Which format specifier is used to print an integer?", options: ["%f", "%c", "%d", "%s"], correct: 2 },
    { id: 3, text: "What is the correct way to declare a pointer?", options: ["int ptr;", "int *ptr;", "int &ptr;", "ptr int;"], correct: 1 },
    { id: 4, text: "Which function is used to allocate memory dynamically?", options: ["malloc()", "alloc()", "new()", "memalloc()"], correct: 0 },
    { id: 5, text: "What is the size of a char data type in C?", options: ["1 byte", "2 bytes", "4 bytes", "8 bytes"], correct: 0 },
    { id: 6, text: "Which operator is used to get the address of a variable?", options: ["*", "->", "&", "&&"], correct: 2 },
    { id: 7, text: "How do you define a constant in C?", options: ["const", "#define", "Both A and B", "constant"], correct: 2 },
    { id: 8, text: "Which loop is guaranteed to execute at least once?", options: ["for", "while", "do-while", "None"], correct: 2 },
    { id: 9, text: "What does 'void' mean in a function return type?", options: ["Returns 0", "Returns null", "Returns nothing", "Returns 1"], correct: 2 },
    { id: 10, text: "Which header file is required for printf()?", options: ["conio.h", "stdlib.h", "stdio.h", "math.h"], correct: 2 }
  ],
  'C++ Programming': [
    { id: 1, text: "Which feature of OOP is used to hide internal details?", options: ["Inheritance", "Encapsulation", "Polymorphism", "Abstraction"], correct: 1 },
    { id: 2, text: "What is the correct way to output data in C++?", options: ["print()", "cout <<", "System.out", "printf()"], correct: 1 },
    { id: 3, text: "Which operator cannot be overloaded?", options: ["+", "::", "==", "<<"], correct: 1 },
    { id: 4, text: "What is a constructor?", options: ["A function to delete objects", "A special method to initialize objects", "A type of variable", "A loop structure"], correct: 1 },
    { id: 5, text: "Which keyword is used for inheritance in C++?", options: ["extends", "implements", ":", "inherits"], correct: 2 },
    { id: 6, text: "What does 'STL' stand for?", options: ["Standard Template Library", "System Type Language", "Simple Tool Library", "Standard Test Logic"], correct: 0 },
    { id: 7, text: "Which of the following is used to create a new object?", options: ["new", "create", "alloc", "make"], correct: 0 },
    { id: 8, text: "A class that cannot be instantiated is called?", options: ["Static class", "Virtual class", "Abstract class", "Base class"], correct: 2 },
    { id: 9, text: "What is the default access specifier in a C++ class?", options: ["public", "protected", "private", "internal"], correct: 2 },
    { id: 10, text: "Which symbol is used for a destructor?", options: ["!", "~", "#", "@"], correct: 1 }
  ],
  'Data Science': [
    { id: 1, text: "Which Python library is primarily used for data manipulation and analysis?", options: ["NumPy", "Matplotlib", "Pandas", "Scikit-Learn"], correct: 2 },
    { id: 2, text: "What does 'Cleaning' data mean?", options: ["Deleting all data", "Removing errors and inconsistencies", "Formatting text", "Moving data to a new file"], correct: 1 },
    { id: 3, text: "In statistics, what is the 'Mean'?", options: ["The middle value", "The most frequent value", "The average value", "The range"], correct: 2 },
    { id: 4, text: "Which chart is best for showing correlations between two variables?", options: ["Bar Chart", "Pie Chart", "Scatter Plot", "Histogram"], correct: 2 },
    { id: 5, text: "What is an 'Outlier'?", options: ["A normal data point", "A missing value", "A value significantly different from others", "The first row"], correct: 2 },
    { id: 6, text: "Which of these is a supervised learning task?", options: ["Clustering", "Dimensionality Reduction", "Regression", "Association"], correct: 2 },
    { id: 7, text: "What is the purpose of a 'Training Set'?", options: ["To test the model", "To build and train the model", "To archive data", "To visualize data"], correct: 1 },
    { id: 8, text: "What does 'Big Data' refer to?", options: ["Large spreadsheets", "Extremely large and complex datasets", "Hard drive storage", "Internet speed"], correct: 1 },
    { id: 9, text: "Which tool is commonly used for data visualization?", options: ["Tableau", "Excel", "PowerBI", "All of the above"], correct: 3 },
    { id: 10, text: "What is a 'Null Hypothesis'?", options: ["A statement of no effect", "A proven fact", "The final result", "An error"], correct: 0 }
  ],
  'SQL': [
    { id: 1, text: "What does SQL stand for?", options: ["Strong Query Language", "Structured Query Language", "Standard Query Language", "System Query Language"], correct: 1 },
    { id: 2, text: "Which command is used to fetch data from a table?", options: ["GET", "OPEN", "SELECT", "FETCH"], correct: 2 },
    { id: 3, text: "Which clause is used to filter records?", options: ["WHERE", "FROM", "ORDER BY", "GROUP BY"], correct: 0 },
    { id: 4, text: "Which command is used to add new data to a table?", options: ["ADD", "INSERT INTO", "UPDATE", "CREATE"], correct: 1 },
    { id: 5, text: "How do you select all columns from a table named 'Users'?", options: ["SELECT all FROM Users", "SELECT Users", "SELECT * FROM Users", "GET * FROM Users"], correct: 2 },
    { id: 6, text: "Which keyword is used to remove duplicate rows?", options: ["UNIQUE", "DISTINCT", "DIFFERENT", "ONLY"], correct: 1 },
    { id: 7, text: "Which SQL statement is used to update data in a database?", options: ["MODIFY", "SAVE", "UPDATE", "CHANGE"], correct: 2 },
    { id: 8, text: "What is a Primary Key?", options: ["A key that opens the database", "A unique identifier for a record", "The first column in a table", "A common password"], correct: 1 },
    { id: 9, text: "Which join returns all records when there is a match in either table?", options: ["INNER JOIN", "LEFT JOIN", "RIGHT JOIN", "FULL OUTER JOIN"], correct: 3 },
    { id: 10, text: "Which function counts the number of rows?", options: ["SUM()", "TOTAL()", "COUNT()", "NUMBER()"], correct: 2 }
  ],
  'Cloud Security': [
    { id: 1, text: "What is the 'Shared Responsibility Model' in Cloud?", options: ["Cloud provider handles everything", "User handles everything", "Provider secures infrastructure; User secures data", "Government regulates security"], correct: 2 },
    { id: 2, text: "What is 'IAM'?", options: ["Internet Access Management", "Identity and Access Management", "Internal Account Monitor", "Instant Alert Messaging"], correct: 1 },
    { id: 3, text: "What is 'SaaS'?", options: ["Security as a Service", "System as a Service", "Software as a Service", "Storage as a Service"], correct: 2 },
    { id: 4, text: "What is a 'VPC'?", options: ["Virtual Private Cloud", "Verified Public Connection", "Variable Power Control", "Virtual Process Core"], correct: 0 },
    { id: 5, text: "Which protocol is used for secure remote login?", options: ["HTTP", "SSH", "FTP", "TELNET"], correct: 1 },
    { id: 6, text: "What is Multi-Factor Authentication (MFA)?", options: ["Using multiple passwords", "Requiring two or more verification methods", "Logging in from multiple devices", "Sharing an account"], correct: 1 },
    { id: 7, text: "What is the 'Least Privilege' principle?", options: ["Giving users maximum access", "Giving users only the access they need", "Restricting all access", "None of the above"], correct: 1 },
    { id: 8, text: "Which of these is a major cloud provider?", options: ["AWS", "Azure", "Google Cloud", "All of the above"], correct: 3 },
    { id: 9, text: "What is 'Data Encryption at Rest'?", options: ["Encrypting data as it moves", "Encrypting data stored on disks", "Encrypting deleted data", "Turning off the server"], correct: 1 },
    { id: 10, text: "What is a 'Cloud Firewall'?", options: ["A hardware box in the office", "A software-based network security service", "A physical fire extinguisher", "A browser plugin"], correct: 1 }
  ],
  'Machine Learning': [
    { id: 1, text: "What are the two main types of Machine Learning?", options: ["Hardware and Software", "Supervised and Unsupervised", "Logical and Physical", "Internal and External"], correct: 1 },
    { id: 2, text: "What is 'Overfitting'?", options: ["Model is too simple", "Model is too complex and fits noise", "Model is perfectly accurate", "Data is missing"], correct: 1 },
    { id: 3, text: "Which algorithm is used for classification tasks?", options: ["Linear Regression", "Logistic Regression", "K-Means", "PCA"], correct: 1 },
    { id: 4, text: "What does 'Neural Network' mimic?", options: ["Computer CPU", "Human Brain", "Electric grid", "Social network"], correct: 1 },
    { id: 5, text: "What is the purpose of 'Validation Data'?", options: ["To train the model", "To tune hyperparameters", "To show to clients", "To store as backup"], correct: 1 },
    { id: 6, text: "Which library is popular for Deep Learning?", options: ["Pandas", "NumPy", "TensorFlow", "Requests"], correct: 2 },
    { id: 7, text: "What is 'Clustering'?", options: ["Predicting a number", "Grouping similar data points", "Sorting a list", "Deleting duplicates"], correct: 1 },
    { id: 8, text: "What is a 'Decision Tree'?", options: ["A plant species", "A flow-chart like structure for decisions", "A way to organize files", "A sorting algorithm"], correct: 1 },
    { id: 9, text: "What does 'NLP' stand for?", options: ["Normal Logic Processing", "Natural Language Processing", "Network Link Protocol", "New Level Programming"], correct: 1 },
    { id: 10, text: "What is 'Reinforcement Learning'?", options: ["Learning from mistakes", "Learning based on rewards and penalties", "Learning without data", "Learning from books"], correct: 1 }
  ],
  'Digital Marketing': [
    { id: 1, text: "What does SEO stand for?", options: ["Social Engine Optimization", "Search Engine Optimization", "Secure Engine Operation", "Site Efficiency Output"], correct: 1 },
    { id: 2, text: "What is 'CTR'?", options: ["Click-Through Rate", "Cost-To-Run", "Customer Total Revenue", "Core Traffic Report"], correct: 0 },
    { id: 3, text: "Which platform is primarily used for B2B marketing?", options: ["Instagram", "Snapchat", "LinkedIn", "TikTok"], correct: 2 },
    { id: 4, text: "What is 'PPC'?", options: ["Pay-Per-Content", "Pay-Per-Click", "Product-Price-Check", "Post-Per-Click"], correct: 1 },
    { id: 5, text: "What is an 'Influencer'?", options: ["A computer hacker", "A person with a large social following", "A brand owner", "A government official"], correct: 1 },
    { id: 6, text: "What is 'Email Marketing'?", options: ["Sending spam only", "Sending targeted messages via email", "Chatting on WhatsApp", "Writing blogs"], correct: 1 },
    { id: 7, text: "What is a 'Conversion'?", options: ["User visiting a site", "User taking a desired action (e.g., buying)", "User closing a site", "User deleting an app"], correct: 1 },
    { id: 8, text: "Which tool is used for tracking website traffic?", options: ["Google Analytics", "Adobe Photoshop", "Microsoft Word", "Slack"], correct: 0 },
    { id: 9, text: "What is 'Content Marketing'?", options: ["Creating valuable content to attract users", "Buying ad space", "Fixing website code", "Designing logos"], correct: 0 },
    { id: 10, text: "What is 'Remarketing'?", options: ["Starting a new business", "Targeting people who visited your site before", "Selling different products", "Changing a brand name"], correct: 1 }
  ],
  'UI/UX Design': [
    { id: 1, text: "What does 'UI' stand for in design?", options: ["User Interface", "User Interaction", "Universal Interface", "User Integration"], correct: 0 },
    { id: 2, text: "Which tool is most commonly used for high-fidelity prototyping?", options: ["Microsoft Paint", "Figma", "Notepad++", "Eclipse"], correct: 1 },
    { id: 3, text: "What is a 'Wireframe'?", options: ["A final colorful design", "A blueprint/skeleton of a screen", "A type of computer virus", "A 3D model of a building"], correct: 1 },
    { id: 4, text: "What does 'UX' stand for?", options: ["User X-factor", "User Experience", "User Extra", "Universal Experience"], correct: 1 },
    { id: 5, text: "Which principle refers to the visual weight and arrangement of elements?", options: ["Typography", "Hierarchy", "Contrast", "All of the above"], correct: 3 },
    { id: 6, text: "What is the primary purpose of a 'User Persona'?", options: ["To design a logo", "To represent a specific type of user", "To hire new employees", "To test website speed"], correct: 1 },
    { id: 7, text: "What is 'White Space' in design?", options: ["An error on the page", "Empty space between elements", "A background image", "Text written in white"], correct: 1 },
    { id: 8, text: "What is the first step in a typical Design Thinking process?", options: ["Prototyping", "Ideating", "Empathize", "Testing"], correct: 2 },
    { id: 9, text: "What is a 'Call to Action' (CTA)?", options: ["A phone call to a customer", "An element that prompts the user to take an action", "A meeting with designers", "A feedback form"], correct: 1 },
    { id: 10, text: "What does 'Accessibility' mean in UX?", options: ["Making a site free to use", "Ensuring people with disabilities can use the product", "Making a site load faster", "Allowing users to download files"], correct: 1 }
  ],
  'App Development': [
    { id: 1, text: "Which language is primarily used for iOS development?", options: ["Java", "Kotlin", "Swift", "C#"], correct: 2 },
    { id: 2, text: "What is 'React Native'?", options: ["A backend database", "A framework for building cross-platform mobile apps", "A native Android tool", "A cloud storage service"], correct: 1 },
    { id: 3, text: "Which platform is Kotlin used for?", options: ["iOS", "Windows", "Android", "macOS"], correct: 2 },
    { id: 4, text: "What does 'API' stand for?", options: ["Application Programming Interface", "App Performance Indicator", "Automated Program Input", "Advanced Processing Integration"], correct: 0 },
    { id: 5, text: "What is an 'Emulator' in mobile development?", options: ["A physical phone", "A software tool that simulates a mobile device", "A type of battery", "A coding editor"], correct: 1 },
    { id: 6, text: "Which file format is used for Android application packages?", options: ["EXE", "IPA", "APK", "DMG"], correct: 2 },
    { id: 7, text: "What is 'Firebase' often used for in apps?", options: ["Designing icons", "Backend services like database and auth", "Compiling code", "Buying domain names"], correct: 1 },
    { id: 8, text: "What is 'Hot Reloading'?", options: ["Rebooting the phone", "Updating the app UI instantly without losing state", "Charging the phone while coding", "Deleting the app cache"], correct: 1 },
    { id: 9, text: "What is the official IDE for Android development?", options: ["Xcode", "Visual Studio", "Android Studio", "Sublime Text"], correct: 2 },
    { id: 10, text: "What is a 'Push Notification'?", options: ["An email from the app", "A message that pops up on a user's device", "A type of bug report", "A file upload"], correct: 1 }
  ],
  'Algorithms': [
    { id: 1, text: "What is a 'Binary Search'?", options: ["A search that checks every element", "A search that divides the list in half each time", "A search for binary numbers only", "A way to delete data"], correct: 1 },
    { id: 2, text: "What is the Time Complexity of a Linear Search?", options: ["O(1)", "O(log n)", "O(n)", "O(n²)"], correct: 2 },
    { id: 3, text: "What is 'Recursion'?", options: ["A loop that never ends", "A function that calls itself", "A way to merge files", "A sorting technique"], correct: 1 },
    { id: 4, text: "Which algorithm is used to find the shortest path in a graph?", options: ["Bubble Sort", "Dijkstra's Algorithm", "Binary Search", "Quick Sort"], correct: 1 },
    { id: 5, text: "What is 'Sorting'?", options: ["Arranging data in a specific order", "Deleting unwanted data", "Searching for a value", "Multiplying numbers"], correct: 0 },
    { id: 6, text: "Which sorting algorithm is generally the fastest?", options: ["Bubble Sort", "Selection Sort", "Quick Sort", "Insertion Sort"], correct: 2 },
    { id: 7, text: "What is the 'Big O' notation used for?", options: ["Counting lines of code", "Describing the efficiency/performance of an algorithm", "Naming variables", "Storing large numbers"], correct: 1 },
    { id: 8, text: "What is a 'Greedy Algorithm'?", options: ["An algorithm that takes more memory", "An algorithm that makes the locally optimal choice at each step", "An algorithm that runs forever", "A type of brute force"], correct: 1 },
    { id: 9, text: "Which data structure is usually used for Breadth-First Search (BFS)?", options: ["Stack", "Queue", "Tree", "Array"], correct: 1 },
    { id: 10, text: "What is 'Dynamic Programming'?", options: ["Writing code while the app is running", "Breaking a problem into overlapping subproblems", "A type of web design", "Using only dynamic variables"], correct: 1 }
  ],
  'Data Structures': [
    { id: 1, text: "Which data structure follows the LIFO (Last In First Out) principle?", options: ["Queue", "Array", "Stack", "Linked List"], correct: 2 },
    { id: 2, text: "What is a 'Linked List'?", options: ["A list of website links", "A collection of nodes containing data and pointers", "A fixed-size array", "A type of database"], correct: 1 },
    { id: 3, text: "In a Tree, what is the top-most node called?", options: ["Leaf", "Branch", "Root", "Parent"], correct: 2 },
    { id: 4, text: "Which data structure follows the FIFO (First In First Out) principle?", options: ["Stack", "Queue", "Tree", "Graph"], correct: 1 },
    { id: 5, text: "What is an 'Array'?", options: ["A collection of elements stored in contiguous memory", "A list of strings only", "A function that returns data", "A dynamic object"], correct: 0 },
    { id: 6, text: "What is a 'Hash Table' used for?", options: ["Sorting data", "Mapping keys to values for fast retrieval", "Connecting to the internet", "Drawing charts"], correct: 1 },
    { id: 7, text: "Which structure is used to represent relationships between nodes using edges?", options: ["Stack", "Queue", "Graph", "Array"], correct: 2 },
    { id: 8, text: "What is a 'Binary Tree'?", options: ["A tree where each node has exactly 2 children", "A tree where each node has at most 2 children", "A tree with binary numbers", "A linear list"], correct: 1 },
    { id: 9, text: "What is the index of the first element in an array in most languages?", options: ["1", "0", "-1", "Any number"], correct: 1 },
    { id: 10, text: "Which data structure is best for undo/redo functionality?", options: ["Queue", "Stack", "Graph", "Tree"], correct: 1 }
  ],
  'Blockchain Core': [
    { id: 1, text: "What is a 'Blockchain'?", options: ["A private database", "A distributed, immutable ledger", "A type of cryptocurrency", "A social media network"], correct: 1 },
    { id: 2, text: "Who is credited with creating Bitcoin?", options: ["Satoshi Nakamoto", "Vitalik Buterin", "Elon Musk", "Bill Gates"], correct: 0 },
    { id: 3, text: "What is a 'Smart Contract'?", options: ["A legal paper", "Self-executing code on a blockchain", "An email agreement", "A digital signature"], correct: 1 },
    { id: 4, text: "What is 'Mining' in blockchain?", options: ["Digging for gold", "Validating transactions and securing the network", "Selling crypto", "Buying hardware"], correct: 1 },
    { id: 5, text: "What does 'Decentralization' mean?", options: ["Controlled by one company", "Controlled by the government", "Not controlled by a single central authority", "Having many servers"], correct: 2 },
    { id: 6, text: "What is a 'Node' in a blockchain?", options: ["A type of coin", "A computer that participates in the network", "A programming bug", "A user account"], correct: 1 },
    { id: 7, text: "What is a 'Genesis Block'?", options: ["The first block in a blockchain", "A block with errors", "The last block in a chain", "A block that contains no data"], correct: 0 },
    { id: 8, text: "Which consensus mechanism is used by Bitcoin?", options: ["Proof of Stake", "Proof of Work", "Proof of Authority", "Delegated Stake"], correct: 1 },
    { id: 9, text: "What is a 'Public Key' used for in crypto?", options: ["As a password", "To receive funds", "To sign transactions", "To hide your identity"], correct: 1 },
    { id: 10, text: "What is 'Ethereum'?", options: ["A digital gold", "A blockchain platform that supports smart contracts", "A type of hardware wallet", "An exchange platform"], correct: 1 }
  ],
  'DevOps': [
    { id: 1, text: "What does DevOps aim to bridge?", options: ["HR and Finance", "Development and Operations", "Design and Marketing", "Sales and Support"], correct: 1 },
    { id: 2, text: "What is 'CI/CD'?", options: ["Constant Input/Constant Data", "Continuous Integration/Continuous Deployment", "Code Improvement/Code Design", "Cloud Infrastructure/Cloud Delivery"], correct: 1 },
    { id: 3, text: "Which tool is commonly used for containerization?", options: ["Docker", "Jenkins", "Git", "Jira"], correct: 0 },
    { id: 4, text: "What is 'Jenkins' used for?", options: ["Designing websites", "Automation server for CI/CD", "Writing database queries", "Editing images"], correct: 1 },
    { id: 5, text: "What is 'Infrastructure as Code' (IaC)?", options: ["Building servers by hand", "Managing infrastructure through machine-readable definition files", "Writing code in the cloud", "None of the above"], correct: 1 },
    { id: 6, text: "Which version control system is most widely used?", options: ["SVN", "Mercurial", "Git", "Bitkeeper"], correct: 2 },
    { id: 7, text: "What is a 'Pipeline' in DevOps?", options: ["A physical water pipe", "A set of automated processes to build and deploy code", "A communication channel", "A bug tracking list"], correct: 1 },
    { id: 8, text: "What is 'Kubernetes' used for?", options: ["Building containers", "Orchestrating and managing containerized applications", "Writing Python code", "Storing user passwords"], correct: 1 },
    { id: 9, text: "What is the primary benefit of 'Automation' in DevOps?", options: ["Increasing manual errors", "Reducing repetitive tasks and increasing speed", "Hiring fewer people", "Making code look better"], correct: 1 },
    { id: 10, text: "Which of these is a cloud platform for DevOps?", options: ["AWS", "Azure DevOps", "Google Cloud", "All of the above"], correct: 3 }
  ],
  'Linux Shell': [
    { id: 1, text: "Which command is used to list files in a directory?", options: ["list", "ls", "show", "dir"], correct: 1 },
    { id: 2, text: "What does 'pwd' stand for?", options: ["PassWord", "Print Working Directory", "Private Web Domain", "Path Way Direction"], correct: 1 },
    { id: 3, text: "How do you create a new directory?", options: ["newdir", "mkdir", "create", "cd"], correct: 1 },
    { id: 4, text: "Which command is used to change directories?", options: ["ch", "go", "cd", "move"], correct: 2 },
    { id: 5, text: "What is 'sudo' used for?", options: ["To delete a file", "To execute a command with administrative privileges", "To search for text", "To restart the shell"], correct: 1 },
    { id: 6, text: "Which command is used to display the contents of a file?", options: ["read", "cat", "view", "open"], correct: 1 },
    { id: 7, text: "How do you remove a file in Linux?", options: ["rm", "del", "remove", "erase"], correct: 0 },
    { id: 8, text: "Which command is used to search for text inside files?", options: ["search", "find", "grep", "look"], correct: 2 },
    { id: 9, text: "What does 'echo' do?", options: ["Repeats the input to the terminal", "Deletes the screen", "Prints the date", "Lists users"], correct: 0 },
    { id: 10, text: "Which key is used for command auto-completion?", options: ["Ctrl", "Shift", "Tab", "Esc"], correct: 2 }
  ],
  'Microsoft Word': [
    { id: 1, text: "What is the default file extension for a Word document?", options: [".txt", ".pdf", ".docx", ".xlsx"], correct: 2 },
    { id: 2, text: "Which shortcut is used to copy text?", options: ["Ctrl+X", "Ctrl+V", "Ctrl+C", "Ctrl+S"], correct: 2 },
    { id: 3, text: "How do you make text bold in Word?", options: ["Ctrl+I", "Ctrl+U", "Ctrl+B", "Ctrl+M"], correct: 2 },
    { id: 4, text: "What is 'Mail Merge' used for?", options: ["Sending emails", "Creating personalized letters for multiple people", "Merging two Word files", "Checking spelling"], correct: 1 },
    { id: 5, text: "Where can you change the page orientation (Portrait/Landscape)?", options: ["Home tab", "Insert tab", "Layout tab", "View tab"], correct: 2 },
    { id: 6, text: "What is a 'Header' in Word?", options: ["The title of the page", "Text that appears at the top of every page", "A type of font", "A bold sentence"], correct: 1 },
    { id: 7, text: "Which tool checks for spelling and grammar errors?", options: ["Thesaurus", "Spelling & Grammar", "Translate", "Word Count"], correct: 1 },
    { id: 8, text: "What is the shortcut for Undo?", options: ["Ctrl+U", "Ctrl+Z", "Ctrl+Y", "Ctrl+X"], correct: 1 },
    { id: 9, text: "How do you insert a Table of Contents?", options: ["Draw it manually", "References tab > Table of Contents", "Insert tab > Links", "Design tab"], correct: 1 },
    { id: 10, text: "What is the 'Format Painter' used for?", options: ["Painting pictures", "Copying formatting from one place to another", "Changing font color", "Resizing images"], correct: 1 }
  ],
  'Microsoft Excel': [
    { id: 1, text: "What is the intersection of a row and a column called?", options: ["Block", "Cell", "Point", "Range"], correct: 1 },
    { id: 2, text: "Every formula in Excel must start with which symbol?", options: ["+", "@", "=", "#"], correct: 2 },
    { id: 3, text: "Which function is used to add a range of numbers?", options: ["TOTAL()", "ADD()", "SUM()", "COUNT()"], correct: 2 },
    { id: 4, text: "What is a 'Pivot Table' used for?", options: ["Creating a drawing", "Summarizing and analyzing large datasets", "Changing font size", "Inserting images"], correct: 1 },
    { id: 5, text: "Which shortcut is used to Save a file?", options: ["Ctrl+S", "Ctrl+V", "Ctrl+P", "Ctrl+N"], correct: 0 },
    { id: 6, text: "What does the 'VLOOKUP' function do?", options: ["Adds numbers", "Searches for a value in a column", "Counts cells", "Creates a chart"], correct: 1 },
    { id: 7, text: "How do you fix a cell reference in a formula (absolute reference)?", options: ["Using $ sign (e.g., $A$1)", "Using % sign", "Using # sign", "Using ! sign"], correct: 0 },
    { id: 8, text: "Which feature automatically fills data based on a pattern?", options: ["AutoPattern", "Flash Fill", "Quick Fill", "Smart Copy"], correct: 1 },
    { id: 9, text: "What is the file extension for Excel workbooks?", options: [".docx", ".pptx", ".xlsx", ".csv"], correct: 2 },
    { id: 10, text: "Which chart is best for showing parts of a whole?", options: ["Line Chart", "Bar Chart", "Pie Chart", "Scatter Plot"], correct: 2 }
  ],
  'Biotechnology Fundamentals': [
    { id: 1, text: "What is the 'building block' of life?", options: ["Atom", "Cell", "Tissue", "DNA"], correct: 1 },
    { id: 2, text: "What does DNA stand for?", options: ["Deoxyribonucleic Acid", "Diribonucleic Acid", "Dinitrogen Acid", "Data Network Analysis"], correct: 0 },
    { id: 3, text: "What is 'PCR' used for in a lab?", options: ["Viewing bacteria", "Amplify/copy DNA segments", "Cleaning equipment", "Storing chemicals"], correct: 1 },
    { id: 4, text: "What is an 'Enzyme'?", options: ["A type of sugar", "A biological catalyst", "A cell wall component", "A harmful gas"], correct: 1 },
    { id: 5, text: "What is 'Fermentation' used for in biotech?", options: ["Freezing cells", "Producing alcohol or acids using microbes", "Building DNA", "Drying samples"], correct: 1 },
    { id: 6, text: "What are 'Genetically Modified Organisms' (GMOs)?", options: ["Naturally grown plants", "Organisms with altered genetic material", "Old species of animals", "Insects"], correct: 1 },
    { id: 7, text: "Which organelle is known as the powerhouse of the cell?", options: ["Nucleus", "Ribosome", "Mitochondria", "Golgi Body"], correct: 2 },
    { id: 8, text: "What is 'Cloning'?", options: ["Deleting a gene", "Producing genetically identical copies", "Breeding two different species", "Microscope observation"], correct: 1 },
    { id: 9, text: "What is the primary role of vaccines?", options: ["To cure an infection immediately", "To train the immune system to recognize pathogens", "To replace blood cells", "To provide energy"], correct: 1 },
    { id: 10, text: "What is a 'Stem Cell'?", options: ["A cell from a plant stem", "An undifferentiated cell that can become various cell types", "A dead cell", "A brain cell only"], correct: 1 }
  ],
  'Bioinformatics Basics': [
    { id: 1, text: "What is 'Bioinformatics'?", options: ["Growing plants in labs", "Using IT and computer science to analyze biological data", "A type of biology textbook", "The study of insects"], correct: 1 },
    { id: 2, text: "What does 'BLAST' stand for in bioinformatics?", options: ["Basic Local Alignment Search Tool", "Biological Language Analysis System", "Bio-Logic Alignment Search Technique", "Binary Local Alignment Search"], correct: 0 },
    { id: 3, text: "What is a 'FASTA' file?", options: ["A fast computer program", "A text-based format for representing sequences", "A database name", "An image file of a cell"], correct: 1 },
    { id: 4, text: "What is 'Sequence Alignment'?", options: ["Sorting DNA by weight", "Comparing sequences to find similarities", "Printing DNA on paper", "Counting the number of cells"], correct: 1 },
    { id: 5, text: "Which database is the main resource for protein sequences?", options: ["NCBI", "UniProt", "GitHub", "Excel"], correct: 1 },
    { id: 6, text: "What is 'Genomics'?", options: ["Study of protein structure", "Study of the complete set of genes (genome)", "Study of bacteria", "Study of human history"], correct: 1 },
    { id: 7, text: "What is a 'Phylogenetic Tree' used for?", options: ["Growing trees in a lab", "Showing evolutionary relationships between species", "Mapping cell structures", "Categorizing chemicals"], correct: 1 },
    { id: 8, text: "What is 'Protein Folding'?", options: ["Cutting a protein", "The physical process by which a protein gets its 3D shape", "Storing proteins in a fridge", "Analyzing protein cost"], correct: 1 },
    { id: 9, text: "What is 'NCBI'?", options: ["National Center for Biotechnology Information", "Network for Cell Biology Integration", "New center for Bioinformatics Insights", "National Chemical Bank of India"], correct: 0 },
    { id: 10, text: "What is 'In silico' analysis?", options: ["Analysis done in a test tube", "Analysis performed on a computer", "Analysis done in nature", "Analysis using silicon chips only"], correct: 1 }
  ],
  'AutoCAD Design': [
    { id: 1, text: "What does 'CAD' stand for?", options: ["Computer-Aided Design", "Computer Analysis and Design", "Central Auto Design", "Complex Architecture Design"], correct: 0 },
    { id: 2, text: "What is the primary file extension for AutoCAD drawings?", options: [".cad", ".dwg", ".dxf", ".pdf"], correct: 1 },
    { id: 3, text: "Which command is used to draw a straight line?", options: ["L", "LINE", "Both L and LINE", "STR"], correct: 2 },
    { id: 4, text: "What is the 'Ortho Mode' used for?", options: ["Drawing circles", "Constraining cursor movement to horizontal and vertical", "Changing colors", "3D viewing"], correct: 1 },
    { id: 5, text: "What is 'Trim' used for?", options: ["Deleting a whole object", "Cutting an object to meet the edge of another", "Extending a line", "Copying an object"], correct: 1 },
    { id: 6, text: "What are 'Layers' in AutoCAD?", options: ["Different sheets of paper", "A way to organize and control visibility of objects", "Types of lines", "3D textures"], correct: 1 },
    { id: 7, text: "Which command is used to round the corners of two lines?", options: ["CHAMFER", "FILLET", "ROUND", "ARC"], correct: 1 },
    { id: 8, text: "What is 'Snap Mode'?", options: ["Taking a screenshot", "Making the cursor jump to specific points on a grid", "Closing the program", "Breaking a line"], correct: 1 },
    { id: 9, text: "Which command creates a duplicate of an object at a specified distance?", options: ["COPY", "OFFSET", "MIRROR", "ARRAY"], correct: 1 },
    { id: 10, text: "What is the 'UCS' in AutoCAD?", options: ["User Control System", "User Coordinate System", "Universal Command System", "Unit Conversion Scale"], correct: 1 }
  ],
  'Hardware & Networking': [
    { id: 1, text: "Which component is the 'Brain' of the computer?", options: ["RAM", "Hard Drive", "CPU", "Motherboard"], correct: 2 },
    { id: 2, text: "What does RAM stand for?", options: ["Read Access Memory", "Random Access Memory", "Rapid Action Memory", "Ready Auto Memory"], correct: 1 },
    { id: 3, text: "What is an 'IP Address'?", options: ["A home address", "A unique identifier for a device on a network", "A website password", "A type of internet speed"], correct: 1 },
    { id: 4, text: "What is the purpose of a 'Router'?", options: ["To store files", "To connect different networks and route traffic", "To type text", "To cool the computer"], correct: 1 },
    { id: 5, text: "What is 'BIOS'?", options: ["Binary Input Output System", "Basic Input Output System", "Background Internet Operating System", "Built-In Operating Software"], correct: 1 },
    { id: 6, text: "Which storage is non-volatile (permanent)?", options: ["RAM", "Cache", "HDD/SSD", "Registers"], correct: 2 },
    { id: 7, text: "What does 'LAN' stand for?", options: ["Large Area Network", "Local Area Network", "Link Access Network", "Log Area Node"], correct: 1 },
    { id: 8, text: "What is the function of a 'Switch' in a network?", options: ["To turn off the internet", "To connect devices within a single network", "To protect against viruses", "To provide wireless signals"], correct: 1 },
    { id: 9, text: "What does 'USB' stand for?", options: ["Universal Serial Bus", "United Serial Board", "Universal System Backup", "User System Bus"], correct: 0 },
    { id: 10, text: "Which port is commonly used for connecting monitors?", options: ["USB", "HDMI", "Ethernet", "PS/2"], correct: 1 }
  ],
  'Network Security': [
    { id: 1, text: "What is the primary goal of Network Security?", options: ["To speed up the internet", "To protect data and networks from unauthorized access", "To build more computers", "To share files faster"], correct: 1 },
    { id: 2, text: "What is a 'Firewall'?", options: ["A physical wall for fires", "A system that filters network traffic", "A type of internet cable", "A computer monitor"], correct: 1 },
    { id: 3, text: "What does 'VPN' stand for?", options: ["Very Private Network", "Virtual Private Network", "Verified Public Node", "Variable Private Network"], correct: 1 },
    { id: 4, text: "What is 'Encryption'?", options: ["Deleting data", "Scrambling data so only authorized parties can read it", "Copying data", "Compressing data"], correct: 1 },
    { id: 5, text: "What is a 'Brute Force Attack'?", options: ["Physically hitting a computer", "Trying every possible password until one works", "Sending a virus via email", "Crashing a website with traffic"], correct: 1 },
    { id: 6, text: "What is 'Malware'?", options: ["A type of hardware", "Malicious software designed to cause harm", "A security guard", "An internet protocol"], correct: 1 },
    { id: 7, text: "What is 'Phishing'?", options: ["Catching fish online", "Deceptive attempt to steal sensitive info via email/links", "A type of encryption", "A hardware repair"], correct: 1 },
    { id: 8, text: "What does 'IDS' stand for?", options: ["Internet Data System", "Intrusion Detection System", "Internal Defense Shield", "Integrated Data Security"], correct: 1 },
    { id: 9, text: "What is 'SSL/TLS' used for?", options: ["Creating logos", "Securing communication between a web browser and a server", "Increasing RAM speed", "Checking hard drive health"], correct: 1 },
    { id: 10, text: "What is 'WPA2'?", options: ["A type of router", "A security protocol for wireless networks", "A fast internet cable", "A computer brand"], correct: 1 }
  ],
  'Generative AI': [
    { id: 1, text: "What is 'Generative AI'?", options: ["AI that only analyzes data", "AI that can create new content like text, images, or music", "AI that is used only for robots", "AI that cannot learn"], correct: 1 },
    { id: 2, text: "What does 'LLM' stand for?", options: ["Large Language Model", "Little Logic Machine", "Live Language Monitor", "Long Learning Module"], correct: 0 },
    { id: 3, text: "Which company developed ChatGPT?", options: ["Google", "Microsoft", "OpenAI", "Meta"], correct: 2 },
    { id: 4, text: "What is a 'Prompt' in Generative AI?", options: ["A type of software bug", "The input/instruction given to an AI model", "The speed of the AI", "A result from the AI"], correct: 1 },
    { id: 5, text: "What is 'Hallucination' in AI?", options: ["The AI seeing ghosts", "When an AI generates false or illogical information confidently", "The AI running out of memory", "A type of AI art"], correct: 1 },
    { id: 6, text: "Which model is primarily used for generating images from text?", options: ["GPT-4", "DALL-E", "BERT", "Word2Vec"], correct: 1 },
    { id: 7, text: "What is 'Tokens' in the context of LLMs?", options: ["Digital currency", "Units of text (words or sub-words) processed by the model", "Security keys", "User accounts"], correct: 1 },
    { id: 8, text: "What is 'Fine-tuning'?", options: ["Cleaning the hardware", "Training a pre-trained model on a specific dataset", "Deleting old data", "Adjusting the volume"], correct: 1 },
    { id: 9, text: "What is 'Transformer' architecture?", options: ["A robot that changes shape", "A neural network architecture used for processing sequences", "A power supply", "A type of graphics card"], correct: 1 },
    { id: 10, text: "What is an 'AI Agent'?", options: ["A person who sells AI", "A system that uses AI to perform tasks autonomously", "A virus", "A chatbot that only says hello"], correct: 1 }
  ],
  'Ruby Programming': [
    { id: 1, text: "In Ruby, which keyword is used to define a method?", options: ["function", "def", "method", "sub"], correct: 1 },
    { id: 2, text: "What is the result of the expression: 'Hi' * 3?", options: ["Hi3", "HiHiHi", "Error", "['Hi', 'Hi', 'Hi']"], correct: 1 },
    { id: 3, text: "Which symbol is used for Symbols in Ruby?", options: ["@symbol", "$symbol", ":symbol", "#symbol"], correct: 2 },
    { id: 4, text: "What does 'nil' represent in Ruby?", options: ["Zero", "False", "An object representing 'nothing'", "An empty string"], correct: 2 },
    { id: 5, text: "How do you define a class variable in Ruby?", options: ["@var", "@@var", "$var", "var"], correct: 1 },
    { id: 6, text: "Which keyword is used to include a module in a class?", options: ["import", "using", "include", "require"], correct: 2 },
    { id: 7, text: "What is the purpose of 'yield' in Ruby?", options: ["To stop a loop", "To return a value", "To execute a block of code within a method", "To create a new thread"], correct: 2 },
    { id: 8, text: "Which of these is the correct syntax for a Hash?", options: ["{ 'a' = 1 }", "[ 'a' => 1 ]", "{ :a => 1 }", "( :a, 1 )"], correct: 2 },
    { id: 9, text: "What is the extension for Ruby files?", options: [".rb", ".ruby", ".ru", ".r"], correct: 0 },
    { id: 10, text: "Which method is used to convert an object to a string?", options: ["to_s", "as_string", "convert_s", "str"], correct: 0 }
  ],
  'R Programming': [
    { id: 1, text: "Which operator is most common for assignment in R?", options: ["=", "<-", ":=", "->"], correct: 1 },
    { id: 2, text: "What is the primary data structure in R for tabular data?", options: ["Matrix", "List", "Data Frame", "Vector"], correct: 2 },
    { id: 3, text: "How do you create a vector in R?", options: ["v(1, 2, 3)", "array(1, 2, 3)", "c(1, 2, 3)", "list(1, 2, 3)"], correct: 2 },
    { id: 4, text: "What does 'NA' represent in R?", options: ["Not Applicable", "Negative Alpha", "Missing Value", "Null Address"], correct: 2 },
    { id: 5, text: "Which function is used to view the first 6 rows of a dataset?", options: ["first()", "top()", "head()", "peek()"], correct: 2 },
    { id: 6, text: "What is the result of 'length(c(1, 2, 3))'?", options: ["1", "2", "3", "6"], correct: 2 },
    { id: 7, text: "Which symbol is used to access columns in a Data Frame by name?", options: ["@", "&", "$", "#"], correct: 2 },
    { id: 8, text: "How do you install a new package in R?", options: ["get_package()", "install.packages()", "library.add()", "pkg_install()"], correct: 1 },
    { id: 9, text: "Which function is used for basic plotting in R?", options: ["draw()", "chart()", "plot()", "graph()"], correct: 2 },
    { id: 10, text: "In R, what is 'summary()' used for?", options: ["To delete data", "To provide statistical summaries of variables", "To close the program", "To save a file"], correct: 1 }
  ],
  'Databases': [
    { id: 1, text: "What does DBMS stand for?", options: ["Data Base Maintenance System", "Database Management System", "Digital Backup Management System", "Data Basic Management Software"], correct: 1 },
    { id: 2, text: "What is a 'Primary Key'?", options: ["A key to open the server room", "A unique identifier for each record in a table", "The first column of any database", "A shared password"], correct: 1 },
    { id: 3, text: "Which language is used to manage relational databases?", options: ["HTML", "Python", "SQL", "Java"], correct: 2 },
    { id: 4, text: "What is 'Normalization'?", options: ["Deleting all data", "Organizing data to reduce redundancy", "Converting numbers to text", "Increasing the database size"], correct: 1 },
    { id: 5, text: "What is a 'Foreign Key'?", options: ["A key used in a different country", "A field that links two tables together", "An encrypted key", "A key that cannot be changed"], correct: 1 },
    { id: 6, text: "What does the 'ACID' property ensure in transactions?", options: ["Speed and size", "Reliability and integrity", "Color and font", "Encryption and security"], correct: 1 },
    { id: 7, text: "Which command is used to remove all records from a table without deleting the table itself?", options: ["DELETE", "DROP", "TRUNCATE", "REMOVE"], correct: 2 },
    { id: 8, text: "What is a 'NoSQL' database?", options: ["A database that doesn't use SQL", "A non-relational database for distributed data", "A database that only stores text", "A broken database"], correct: 1 },
    { id: 9, text: "What is a 'Schema'?", options: ["A database error", "The logical structure or blueprint of a database", "A type of query", "A backup file"], correct: 1 },
    { id: 10, text: "Which join returns only the rows where there is a match in both tables?", options: ["LEFT JOIN", "RIGHT JOIN", "INNER JOIN", "FULL JOIN"], correct: 2 }
  ]
  };

  // Get questions for the selected course (always 10 questions)
  const questions = questionBank[decodedCourseName] || questionBank['Web Development'];
  const TOTAL_QUESTIONS = 10; // Fixed number of questions

  // Timer Effect
  useEffect(() => {
    if (quizStarted && !quizCompleted && !showResults && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else if (quizStarted && timeLeft === 0 && !quizCompleted && !showResults) {
      handleTimeOut();
    }
  }, [timeLeft, quizCompleted, showResults, quizStarted]);

  const startQuiz = () => {
    setQuizStarted(true);
    setTimeLeft(30);
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setSubmitError('');
    setSubmitSuccess(false);
  };

  const handleTimeOut = () => {
    if (currentQuestion < TOTAL_QUESTIONS - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setTimeLeft(30);
    } else {
      calculateScore();
    }
  };

  const handleAnswerSelect = (optionIndex) => {
    // 1. Create the new answers object immediately
    const newAnswers = {
      ...selectedAnswers,
      [currentQuestion]: optionIndex
    };
    
    // 2. Update the state (for the UI to show the selected bubble)
    setSelectedAnswers(newAnswers);
    
    setTimeout(() => {
      if (currentQuestion < TOTAL_QUESTIONS - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setTimeLeft(30);
      } else {
        // 3. Pass the guaranteed latest answers directly to calculateScore
        calculateScore(newAnswers);
      }
    }, 500);
  };

  const calculateScore = (finalAnswers = selectedAnswers) => {
    let correctCount = 0;
    const review = [];
    
    questions.forEach((question, index) => {
      // 4. Use finalAnswers here to ensure the 10th answer is counted
      const userAnswer = finalAnswers[index];
      const isCorrect = userAnswer === question.correct;
      if (isCorrect) correctCount++;
      
      review.push({
        question: question.text,
        userAnswer: userAnswer !== undefined ? question.options[userAnswer] : "No answer",
        correctAnswer: question.options[question.correct],
        isCorrect: isCorrect
      });
    });
    
    setScore(correctCount);
    setAnswersReview(review);
    setQuizCompleted(true);
    
    // Auto-submit results to backend
    submitExamResults(correctCount);
  };

  const submitExamResults = async (correctCount) => {
    setIsSubmitting(true);
    setSubmitError('');
    
    const wrongCount = TOTAL_QUESTIONS - correctCount;
    
    const requestBody = {
      exam_name: decodedCourseName,
      total_questions: TOTAL_QUESTIONS,
      correct_answers: correctCount,
      wrong_answers: wrongCount
    };
    
    console.log('Submitting exam results:', requestBody);
    
    try {
      const response = await senvraApi.post('/dashboard', requestBody);
      console.log('Exam submission response:', response.data);
      
      setSubmitSuccess(true);
      setCertificateInfo(response.data.certificate);
      
      // Show results after successful submission
      setShowResults(true);
      
    } catch (error) {
      console.error('Error submitting exam:', error);
      
      let errorMsg = 'Failed to save exam results. ';
      if (error.response) {
        errorMsg += error.response.data?.detail || 'Please try again.';
      } else if (error.request) {
        errorMsg += 'Network error. Please check your connection.';
      } else {
        errorMsg += 'An unexpected error occurred.';
      }
      
      setSubmitError(errorMsg);
      
      // Still show results even if API fails
      setShowResults(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetakeQuiz = () => {
    setQuizStarted(false);
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setTimeLeft(30);
    setQuizCompleted(false);
    setShowResults(false);
    setScore(0);
    setAnswersReview([]);
    setSubmitError('');
    setSubmitSuccess(false);
    setCertificateInfo(null);
  };

  const handleBackToCourses = () => {
    navigate('/browse-courses');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Start Quiz Screen
  if (!quizStarted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 text-center">
              <Award className="w-24 h-24 text-white mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-white mb-2">{decodedCourseName}</h1>
              <p className="text-blue-100">Skill Assessment Quiz</p>
            </div>
            
            <div className="p-8">
              <div className="space-y-4 mb-8">
                <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                  <span className="text-slate-600">Total Questions</span>
                  <span className="font-bold text-slate-800">{TOTAL_QUESTIONS}</span>
                </div>
                <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                  <span className="text-slate-600">Time per Question</span>
                  <span className="font-bold text-slate-800">30 seconds</span>
                </div>
                <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                  <span className="text-slate-600">Passing Score</span>
                  <span className="font-bold text-green-600">50% (5/10)</span>
                </div>
                <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                  <span className="text-slate-600">Certificate</span>
                  <span className="font-bold text-blue-600">✓ Verified on Passing</span>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
                <p className="text-amber-800 text-sm flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span>⚠️ Once you start, you cannot pause the quiz. Each question has a 30-second timer. Score 60% or above to earn your certificate!</span>
                </p>
              </div>

              <button
                onClick={startQuiz}
                className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-bold text-lg hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Play className="w-5 h-5" />
                Start Quiz Now
              </button>

              <button
                onClick={handleBackToCourses}
                className="w-full mt-3 py-3 border-2 border-slate-300 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition"
              >
                Back to Courses
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Results Screen
  if (showResults) {
    const percentage = (score / TOTAL_QUESTIONS) * 100;
    const passed = percentage >= 60;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className={`p-8 text-center ${passed ? 'bg-emerald-50' : 'bg-red-50'}`}>
              {passed ? (
                <Award className="w-20 h-20 text-emerald-500 mx-auto mb-4" />
              ) : (
                <AlertCircle className="w-20 h-20 text-red-500 mx-auto mb-4" />
              )}
              <h2 className="text-3xl font-bold text-slate-800 mb-2">
                {passed ? "Congratulations!" : "Keep Learning!"}
              </h2>
              <p className="text-slate-600">
                {passed 
                  ? `You've successfully completed the ${decodedCourseName} assessment!` 
                  : "Don't worry! Review the material and try again."}
              </p>
            </div>

            {/* Submission Status */}
            {isSubmitting && (
              <div className="p-4 bg-blue-50 text-blue-600 text-center flex items-center justify-center gap-2">
                <Loader2 className="animate-spin w-4 h-4" />
                Saving your results...
              </div>
            )}
            
            {submitError && (
              <div className="p-4 bg-red-50 text-red-600 text-center">
                ⚠️ {submitError}
              </div>
            )}
            
            {submitSuccess && passed && (
              <div className="p-4 bg-emerald-50 text-emerald-600 text-center">
  🎉 Certificate generated successfully! Check your{" "}
  <span
    onClick={() => navigate("/dashboards")}
    className="cursor-pointer underline font-medium"
  >
    dashboard
  </span>.
</div>
            )}

            <div className="p-8 border-b border-slate-100">
              <div className="text-center mb-6">
                <div className="text-6xl font-bold text-blue-600 mb-2">
                  {score}/{TOTAL_QUESTIONS}
                </div>
                <div className="text-lg text-slate-600">Your Score</div>
                <div className="mt-3 inline-block px-4 py-2 rounded-full text-sm font-bold" style={{
                  backgroundColor: passed ? '#d1fae5' : '#fee2e2',
                  color: passed ? '#065f46' : '#991b1b'
                }}>
                  {passed ? "PASSED" : "NOT PASSED"} ({percentage.toFixed(0)}%)
                </div>
                {passed && certificateInfo && (
                  <div className="mt-3 inline-block ml-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-full text-sm font-bold">
                    🎓 Certificate ID: {certificateInfo.certificate_id}
                  </div>
                )}
              </div>

              <div className="w-full bg-slate-200 rounded-full h-2 mb-6">
                <div 
                  className={`h-2 rounded-full transition-all duration-500 ${passed ? 'bg-emerald-500' : 'bg-red-500'}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
              
              {/* Passing requirement notice */}
              <div className="text-center text-sm text-slate-500">
                {passed ? (
                  <p>✅ You have earned a verified certificate! You can download it from your dashboard.</p>
                ) : (
                  <p>⚠️ You need {6 - score} more correct answers to pass. Required: 6 out of 10 (60%)</p>
                )}
              </div>
            </div>

            <div className="p-8">
              <h3 className="text-xl font-bold text-slate-800 mb-6">Answer Review</h3>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {answersReview.map((item, index) => (
                  <div key={index} className="border border-slate-200 rounded-xl p-4 hover:shadow-md transition">
                    <div className="flex items-start gap-3">
                      {item.isCorrect ? (
                        <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        <p className="font-semibold text-slate-800 mb-2">
                          Question {index + 1}: {item.question}
                        </p>
                        <div className="space-y-1 text-sm">
                          <p className={item.isCorrect ? 'text-emerald-700' : 'text-red-700'}>
                            Your answer: {item.userAnswer}
                          </p>
                          {!item.isCorrect && (
                            <p className="text-emerald-700">
                              Correct answer: {item.correctAnswer}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-8 bg-slate-50 flex gap-4">
              <button
                onClick={handleRetakeQuiz}
                className="flex-1 py-3 px-6 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition shadow-md"
              >
                Retake Quiz
              </button>
              <button
                onClick={handleBackToCourses}
                className="flex-1 py-3 px-6 border-2 border-slate-300 text-slate-700 rounded-xl font-bold hover:bg-slate-100 transition"
              >
                Back to Courses
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Active Quiz View
  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion) / TOTAL_QUESTIONS) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">{decodedCourseName}</h1>
              <p className="text-slate-500 text-sm">Skill Assessment Quiz</p>
            </div>
            <div className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-full">
              <Clock className="w-4 h-4 text-slate-600" />
              <span className={`font-mono font-bold ${timeLeft <= 10 ? 'text-red-600 animate-pulse' : 'text-slate-700'}`}>
                {formatTime(timeLeft)}
              </span>
            </div>
          </div>
          
          <div className="mb-2">
            <div className="flex justify-between text-sm text-slate-500 mb-1">
              <span>Progress</span>
              <span>{currentQuestion + 1} of {TOTAL_QUESTIONS}</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-bold">{currentQuestion + 1}</span>
              </div>
              <span className="text-slate-500 text-sm">Question</span>
            </div>
            <h2 className="text-xl font-semibold text-slate-800 leading-relaxed">
              {currentQ.text}
            </h2>
          </div>

          <div className="space-y-3">
            {currentQ.options.map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleAnswerSelect(idx)}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-md ${
                  selectedAnswers[currentQuestion] === idx
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    selectedAnswers[currentQuestion] === idx
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-slate-300'
                  }`}>
                    {selectedAnswers[currentQuestion] === idx && (
                      <div className="w-2 h-2 bg-white rounded-full" />
                    )}
                  </div>
                  <span className="text-slate-700">{option}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              <span>Select an answer to continue</span>
            </div>
            <div className="flex items-center gap-2">
              <span>⏱️ {timeLeft} seconds left</span>
            </div>
          </div>
        </div>

        {timeLeft <= 10 && timeLeft > 0 && (
          <div className="fixed bottom-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg animate-bounce">
            ⚠️ Hurry! Only {timeLeft} seconds left!
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseQuiz;